import React, { useEffect, useState, useMemo } from 'react';
import UserName from '../../common/user/UserName';
import styles from './DashboardListing.module.css';
import ConfiguredPMS from '../configuredPMS/ConfiguredPMS';
import SupportTicketsCard from '../supportTicketsCard/SupportTicketsCard';
import UpcomingCard from '../upcomingCard/UpcomingCard';
import { Link } from 'react-router-dom';
import { AnyIfEmpty, useDispatch, useSelector } from 'react-redux';
import { AppDispatch, IRootState } from '../../../redux/rootReducer';
import { getSupportTickets } from '../../../redux/support-ticket/actions/support-ticket.action';
import moment from "moment";
import { getPropertyEvents } from "../../../redux/key-pickup-drop-events/actions/key-pickup-drop-events.action";
import Loader from '../../common/loader/Loader';
import Select from 'react-select';
import { getLocation, getLocationById } from '../../../redux/location/actions/location.action';
import { locationsData } from '../../locations/Locations';
import { getVisits } from '../../../redux/visits/actions/visits.action';

/** Key pickup/drop event from getPropertyEvents API */
interface PropertyEvent {
    startDate: string;
    startTime: string;
    endDate?: string;
    endTime?: string;
    eventName?: string;
    name?: string;
    locationName?: string;
    deletedAt?: string | null;
    isDeleted?: boolean;
}

/** Visit/appointment item from getVisits API (appointment array) */
interface VisitAppointment {
    unitName?: string;
    unitYardiId?: string;
    locker_number?: number;
    appointmentDate?: string;
    appointmentStartTime?: string;
    appointmentEndTime?: string;
    hasAdminDeleted?: number;
    deletedAt?: string | null;
    firstName?: string;
    lastName?: string;
}

function DashboardListing() {
    const dispatch = useDispatch<AppDispatch>();
    const supportTickets = useSelector((state: IRootState) => state.supportTicketReducer.data);
    const loaderSupportTickets = useSelector((state: IRootState) => state.supportTicketReducer.loading);
    const loaderPropertyEvents = useSelector((state: IRootState) => state.propertyEventReducer.loading);
    const visitsLoading = useSelector((state: IRootState) => state.appointmentReducer.loading);
    const visitsData = useSelector((state: IRootState) => state.appointmentReducer.data);
    const visits = useMemo(() => {
        const d = visitsData as { appointment?: VisitAppointment[]; data?: { appointment?: VisitAppointment[] } } | undefined;
        return d?.appointment ?? d?.data?.appointment ?? [];
    }, [visitsData]);

    const allLocationsList = useSelector((state: IRootState) => state.locationReducer.data.locations);
    const invitedLocations = useSelector((state: IRootState) => state.locationReducer.data?.locationinvitedlist);
    const locationLoading = useSelector((state: IRootState) => state.locationReducer.loading);
    const locationDetail = useSelector((state: IRootState) => state.locationReducer.location?.data);
    const locationDetailLoading = useSelector((state: IRootState) => state.locationReducer.location?.loading);

    const [locations, setLocations] = useState<locationsData[]>([]);
    const [selectedLocation, setSelectedLocation] = useState<{ value: string; label: string }>({
        value: '',
        label: '',
    });

    const propertyEvents = useSelector((state: IRootState) => state.propertyEventReducer.data);

    useEffect(() => {
        dispatch(getLocation(undefined, undefined, true));
    }, []);

    useEffect(() => {
        if (locations.length > 0 && !selectedLocation.value) {
            const firstLocation = locations[0];
            setSelectedLocation({
                value: firstLocation.id || '',
                label: firstLocation.name || '',
            });
        }
    }, [locations]);

    useEffect(() => {
        if (selectedLocation.value) {
            dispatch(getLocationById(selectedLocation.value));
            // Wide range so dashboard shows all ongoing + future appointments (not only current week)
            const startDate = moment().subtract(1, "month").format("YYYY-MM-DD");
            const endDate = moment().add(12, "months").format("YYYY-MM-DD");
            dispatch(
                getVisits(
                    selectedLocation.value,
                    startDate,
                    endDate
                )
            );
            dispatch(
                getPropertyEvents({
                    search: "",
                    page: 1,
                    limit: 50,
                    setTotalCount: () => { },
                    locationId: selectedLocation.value,
                })
            );
            dispatch(
                getSupportTickets({
                    searchText: "",
                    page: 1,
                    limit: 20,
                    location: [selectedLocation.value],
                })
            );
        }
    }, [selectedLocation.value, dispatch]);

    useEffect(() => {
        if (!invitedLocations?.length && !allLocationsList?.length) {
            setLocations([]);
        } else {
            if (invitedLocations?.length || allLocationsList?.length) {
                const data = invitedLocations?.map((list: locationsData) => ({
                    ...list,
                    isRestricted: true,
                }));
                setLocations([...(data ?? []), ...(allLocationsList ?? [])]);
            }
        }
    }, [allLocationsList?.length, invitedLocations?.length]);

    const locationOptions = useMemo(
        () =>
            locations.map((loc: locationsData) => ({
                value: loc.id ?? '',
                label: loc.name ?? '',
            })),
        [locations]
    );

    const dateTimeFormats = [
        "YYYY-MM-DD HH:mm A",
        "YYYY-MM-DD hh:mm A",
        "YYYY-MM-DD h:mm A",
        "YYYY-MM-DD H:mm",
        "YYYY-MM-DD HH:mm",
        "YYYY-MM-DD hh:mm",
    ];

    const upcomingKeyEvents = useMemo(() => {
        const now = moment();
        return ((propertyEvents ?? []) as PropertyEvent[])
            .filter((event: PropertyEvent) => {
                // Exclude deleted property events
                if (event.deletedAt != null || event.isDeleted === true) return false;
                const startMoment = moment(
                    `${event.startDate} ${event.startTime}`,
                    dateTimeFormats
                );
                if (!startMoment.isValid()) return false;
                // Ongoing: start <= now <= end. Future: start > now. Show if end >= now.
                const endMoment =
                    event.endDate && event.endTime
                        ? moment(`${event.endDate} ${event.endTime}`, dateTimeFormats)
                        : startMoment;
                const eventEnd = endMoment.isValid() ? endMoment : startMoment;
                const isOngoing = startMoment.isSameOrBefore(now, "minute") && eventEnd.isSameOrAfter(now, "minute");
                const isFuture = startMoment.isAfter(now, "minute");
                return isOngoing || isFuture;
            })
            .sort((a: PropertyEvent, b: PropertyEvent) =>
                moment(`${a.startDate} ${a.startTime}`).diff(
                    moment(`${b.startDate} ${b.startTime}`)
                )
            )
            .slice(0, 4);
    }, [propertyEvents]);

    const appointmentDateTimeFormats = [
        "YYYY-MM-DD hh:mm A",
        "YYYY-MM-DD h:mm A",
        "YYYY-MM-DD HH:mm",
        "YYYY-MM-DD H:mm",
        "YYYY-MM-DD HH:mm A",
        "YYYY-MM-DD hh:mm",
    ];

    const upcomingAppointments = useMemo(() => {
        const now = moment();
        const parseAppointmentMoment = (v: VisitAppointment, useEndTime = false) => {
            const dateStr = v.appointmentDate;
            const timeStr = useEndTime ? (v.appointmentEndTime ?? v.appointmentStartTime) : v.appointmentStartTime;
            if (!dateStr) return null;
            if (timeStr) {
                const m = moment(`${dateStr} ${timeStr}`, appointmentDateTimeFormats);
                return m.isValid() ? m : moment(dateStr, "YYYY-MM-DD");
            }
            return moment(dateStr, "YYYY-MM-DD");
        };
        return (visits as VisitAppointment[])
            .filter((v) => {
                if (v.hasAdminDeleted === 1 || (v.deletedAt != null && v.deletedAt !== "")) return false;
                const startM = parseAppointmentMoment(v, false);
                const endM = parseAppointmentMoment(v, true);
                if (!startM?.isValid()) return false;
                const appointmentEnd = endM?.isValid() ? endM : startM;
                // Show ongoing (start <= now <= end) or future (start > now)
                const isOngoing = startM.isSameOrBefore(now, "minute") && appointmentEnd.isSameOrAfter(now, "minute");
                const isFuture = startM.isAfter(now, "minute");
                return isOngoing || isFuture;
            })
            .sort((a, b) => {
                const ma = parseAppointmentMoment(a);
                const mb = parseAppointmentMoment(b);
                if (!ma || !mb) return 0;
                return ma.diff(mb);
            })
            .map((v) => {
                const m = parseAppointmentMoment(v);
                const formatTime = (t: string | undefined) =>
                    t ? moment(t, ["HH:mm", "hh:mm A", "h:mm A"]).format("hh:mm A") : null;
                const startTime = formatTime(v.appointmentStartTime);
                const endTime = formatTime(v.appointmentEndTime);
                const time =
                    startTime && endTime
                        ? `${startTime} - ${endTime}`
                        : startTime ?? undefined;
                return {
                    title: selectedLocation.label || 'Appointment',
                    subtitle: [v.unitYardiId, v.unitName].filter(Boolean).join(' ') || '—',
                    date: m ? m.format("MM-DD-YYYY") : '',
                    time,
                    locationName: selectedLocation.label,
                    firstname: v.firstName,
                    lastname: v.lastName,
                };
            });
    }, [visits, selectedLocation.label]);

    const colourStyles = {
        control: (base: AnyIfEmpty<object>, state: AnyIfEmpty<{ isFocused: boolean }>) => ({
            ...base,
            height: 44,
            minHeight: 44,
            border: `1px solid ${state?.isFocused ? '#51ab86' : '#d1d2d3'}`,
            borderRadius: 8,
            boxShadow: state?.isFocused ? '0 0 0 2px rgba(81, 171, 134, 0.2)' : 'none',
            outline: 'none',
        }),
        menu: (base: AnyIfEmpty<object>) => ({
            ...base,
            zIndex: 10,
            marginTop: 0,
            border: '1px solid #d1d2d3',
            borderTop: 'none',
            borderRadius: '0 0 8px 8px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
        }),
        menuList: (base: AnyIfEmpty<object>) => ({
            ...base,
            padding: '4px 0',
            maxHeight: 240,
        }),
        option: (base: AnyIfEmpty<object>, { isFocused, isSelected }: AnyIfEmpty<object>) => ({
            ...base,
            padding: '10px 12px',
            fontSize: 14,
            backgroundColor: isSelected ? '#51ab86' : isFocused ? '#f0fdf4' : undefined,
            color: isSelected ? '#fff' : '#191c21',
            cursor: 'default',
        }),
        placeholder: (base: AnyIfEmpty<object>) => ({
            ...base,
            color: '#6b7280',
        }),
        singleValue: (base: AnyIfEmpty<object>) => ({
            ...base,
            color: '#191c21',
        }),
        dropdownIndicator: (base: AnyIfEmpty<object>) => ({
            ...base,
            color: '#6b7280',
            padding: '8px 12px',
            '&:hover': { color: '#51ab86' },
        }),
        indicatorSeparator: () => ({ display: 'none' }),
        valueContainer: (base: AnyIfEmpty<object>) => ({
            ...base,
            padding: '8px 12px',
        }),
        input: (base: AnyIfEmpty<object>) => ({
            ...base,
            margin: 0,
            padding: 0,
            color: '#191c21',
        }),
        loadingIndicator: (base: AnyIfEmpty<object>) => ({
            ...base,
            color: '#51ab86',
        }),
    };

    const isDashboardLoading =
        locationLoading ||
        loaderSupportTickets ||
        loaderPropertyEvents ||
        visitsLoading ||
        locationDetailLoading;

    if (isDashboardLoading) {
        return <Loader />;
    }

    return (
        <React.Fragment>
            <div className={`${styles.dashboardPadding} outletConainer1`}>
                <div className={styles.dashboardHead}>
                    <div className={styles.userGreet}>
                        <p>
                            Welcome <UserName />
                        </p>
                        <h1>Dashboard</h1>
                    </div>
                    <div>
                        <div className={styles.filterDropDown}>
                            <Select
                                onChange={(selectedOption) => {
                                    if (selectedOption?.value) {
                                        setSelectedLocation(selectedOption);
                                    }
                                }}
                                isSearchable={true}
                                classNamePrefix="custom-react-select"
                                value={
                                    selectedLocation.value !== ''
                                        ? { value: selectedLocation.value, label: selectedLocation.label }
                                        : undefined
                                }
                                options={locationOptions}
                                placeholder="Select Location"
                                styles={colourStyles}
                                isLoading={locationLoading}
                            />
                        </div>
                    </div>

                </div>


                <div className={styles.dashboardContent}>
                    <div className={styles.dashboardContentLeft}>
                        <ConfiguredPMS
                            selectedLocationId={selectedLocation.value}
                            pmsConfig={(() => {
                                const loc = locationDetail?.location ?? locationDetail?.data?.location;
                                if (!loc || String(loc.id) !== String(selectedLocation.value)) return undefined;
                                return loc.pmsConfig;
                            })()}
                            pmsLogs={locationDetail?.pmsLogs ?? []}
                            loading={locationDetailLoading}
                        />

                        <div className={styles.upcomingCardBlockWrap} >
                            <div className={styles.upcomingCardBlock}>
                                <div className={styles.upcomingCardHead}>
                                    <h2>Upcoming Appointments</h2>


                                    <Link to="/visits" className={styles.viewAll}>View All</Link>
                                </div>
                                <div className={`${styles.upcomingCardWrap} ${styles.upcomingCardWrapScroll}`}>
                                    {upcomingAppointments.length > 0 ? (
                                        upcomingAppointments.map((event, index) => (
                                            <UpcomingCard
                                                key={`${event.date}-${event.subtitle}-${index}`}
                                                title={event.title}
                                                subtitle={event.subtitle}
                                                date={event.date}
                                                time={event.time}
                                                locationName={event.locationName}
                                                firstname={event.firstname}
                                                lastname={event.lastname}
                                            />
                                        ))
                                    ) : selectedLocation.value && !visitsLoading ? (
                                        <div className={styles.noEvents}>
                                            <p>No upcoming appointments</p>
                                        </div>
                                    ) : null}
                                </div>
                            </div>
                            <div className={styles.upcomingCardBlock}>
                                <div className={styles.upcomingCardHead}><h2>Upcoming Key PickUp & Drop</h2> <Link to="/key-pickup-drop" className={styles.viewAll}>View All</Link></div>
                                <div className={`${styles.upcomingCardWrap} ${styles.upcomingCardWrapScroll}`}>
                                    {upcomingKeyEvents && upcomingKeyEvents.length > 0 ? (
                                        upcomingKeyEvents.map((event: PropertyEvent, index: number) => {
                                            // Format time range like Upcoming Appointments: "h:mm A - h:mm A" or with end date if different
                                            const startDateFormatted = moment(event.startDate).format("MM-DD-YYYY");
                                            const startTimeFormatted = event.startTime
                                                ? moment(event.startTime, ["HH:mm", "hh:mm A", "h:mm A"]).format("h:mm A")
                                                : "";
                                            const endTimeFormatted =
                                                event.endTime
                                                    ? moment(event.endTime, ["HH:mm", "hh:mm A", "h:mm A"]).format("h:mm A")
                                                    : null;
                                            const endDateFormatted =
                                                event.endDate ? moment(event.endDate).format("MM-DD-YYYY") : null;
                                            const isSameDay =
                                                event.endDate && moment(event.startDate).isSame(moment(event.endDate), "day");
                                            const timeStr =
                                                startTimeFormatted && endTimeFormatted
                                                    ? isSameDay || !endDateFormatted
                                                        ? `${startTimeFormatted} - ${startDateFormatted}, ${endTimeFormatted}`
                                                        : `${startTimeFormatted} - ${endDateFormatted}, ${endTimeFormatted}`
                                                    : startTimeFormatted || undefined;
                                            return (
                                                <UpcomingCard
                                                    key={index}
                                                    title={event.eventName ?? ''}
                                                    subtitle={[event.locationName, event.name].filter(Boolean).join(", ")}
                                                    locationName={event.locationName}
                                                    date={startDateFormatted}
                                                    time={timeStr}
                                                />
                                            );
                                        })
                                    ) : selectedLocation.value && !loaderPropertyEvents ? (
                                        <div className={styles.noEvents}>
                                            <p>No upcoming key pick up & drop events</p>
                                        </div>
                                    ) : null}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className={styles.dashboardContentRight}>
                        <SupportTicketsCard
                            supportTickets={supportTickets}
                            loading={loaderSupportTickets}
                            hasSelectedLocation={!!selectedLocation.value}
                        />
                    </div>
                </div>
            </div>
        </React.Fragment>
    );
}

export default DashboardListing;  
